<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, onMounted, ref, watch } from 'vue'
import WinnersContent from '@/partners/lingo/lingo-giveaway/components/winners/WinnersContent.vue'
import WinnerContactForm from '@/partners/lingo/lingo-giveaway/components/winners/WinnerContactForm.vue'
import LoserContent from '@/partners/lingo/lingo-giveaway/components/winners/LoserContent.vue'
import { getSubmissions, getUserWinsWithState } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'
import type { WinnerContact, WonPrize } from '@/partners/lingo/lingo-giveaway/types/giveaway'
import PrizesContent from '@/partners/lingo/lingo-giveaway/components/winners/PrizesContent.vue'

enum WinnersModalStates {
  LIST = 'list',
  WINNER = 'winner',
  LOSER = 'loser',
  PRIZES = 'prizes',
}

const props = defineProps<{
  show: boolean,
}>()

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const modalState = ref<WinnersModalStates>(WinnersModalStates.LIST)

const playersPrizes = ref<number[]>([])
const playersContact = ref<WinnerContact | null>(null)

const isWinner = computed(() => !!playersPrizes.value.length)

const wonPrizes = ref<WonPrize[]>([])

function onStatusCheck() {
  if (isWinner.value) {
    modalState.value = WinnersModalStates.WINNER
  } else {
    modalState.value = WinnersModalStates.LOSER
  }
}

function onHide() {
  emit('hide')
  modalState.value = WinnersModalStates.LIST
}

async function syncWins() {
  const { prizes: newPrizes, contactData } = await getSubmissions()
  playersPrizes.value = newPrizes
  playersContact.value = contactData
  wonPrizes.value = await getUserWinsWithState()
}

onMounted(async () => {
  await syncWins()
})

watch(() => props.show, async () => {
  if (props.show) {
    await syncWins()
  }
})

</script>
<template>
  <TheBottomSheet
    :show="show"
    content-class="flex flex-col pb-0"
    scrollable
    @hide="onHide"
  >
    <WinnersContent
      v-if="modalState === WinnersModalStates.LIST"
      :has-prizes="!!wonPrizes.length"
      @on-check="onStatusCheck"
      @on-prizes="modalState = WinnersModalStates.PRIZES"
    />
    <WinnerContactForm
      v-if="modalState === WinnersModalStates.WINNER"
      :won-prizes="playersPrizes"
      :contact-data="playersContact"
      @on-submit="onHide"
    />
    <LoserContent
      v-if="modalState === WinnersModalStates.LOSER"
      @on-back="onHide"
    />
    <PrizesContent
      v-if="modalState === WinnersModalStates.PRIZES"
      :won-prizes="wonPrizes"
      @return="modalState = WinnersModalStates.LIST"
    />
  </TheBottomSheet>
</template>